import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { PERSON_PHONE_CONTACT_TYPES, type PersonContactResponse } from '@tabita/contracts';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { peopleApi } from '@/services/people.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, Field } from '@/components/ui';

type PhoneContactType = (typeof PERSON_PHONE_CONTACT_TYPES)[number];

/**
 * "Sección de Contacto" — the repeatable phone list (Casa/Trabajo/Celular).
 * Same placement and save-independently-of-the-edit-form posture as
 * `HouseholdCard`/`RelationshipsCard` on this same tab: each add/remove
 * hits its own endpoint immediately, there is no batch "Guardar" for this
 * section. The three named emails live as ordinary fields on
 * `PersonEditForm` instead — a fixed identity, not a repeatable list.
 */
export function ContactsCard({
  personId,
  contacts,
  onChanged,
}: {
  readonly personId: string;
  readonly contacts: readonly PersonContactResponse[];
  readonly onChanged: () => void;
}) {
  const { t } = useI18n();
  const { can } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [type, setType] = useState<PhoneContactType>('MOBILE');
  const [value, setValue] = useState('');

  const canManage = can('people.person.update');
  const phones = contacts.filter((contact) =>
    (PERSON_PHONE_CONTACT_TYPES as readonly string[]).includes(contact.type),
  );

  const add = useMutation({
    mutationFn: () =>
      peopleApi.addContact(personId, { type, value: value.trim(), isPrimary: false }),
    onSuccess: () => {
      setValue('');
      setError(null);
      onChanged();
    },
    onError: (err: unknown) => setError(describeError(err, t)),
  });

  const remove = useMutation({
    mutationFn: (contactId: string) => peopleApi.removeContact(personId, contactId),
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: (err: unknown) => setError(describeError(err, t)),
  });

  if (!canManage && phones.length === 0) return null;

  return (
    <section className="detail-section">
      <h2 className="detail-section-title">{t('people.contact.title')}</h2>

      {error && <Alert tone="danger">{error}</Alert>}

      {phones.length === 0 ? (
        <p className="cell-empty">{t('common.states.empty')}</p>
      ) : (
        <ul className="people-relation-list">
          {phones.map((contact) => (
            <li key={contact.id} className="member-row">
              <span>{t(`people.person.contactType.${contact.type}`)}</span>
              <span className="cell-mono" dir="ltr">
                {contact.value}
              </span>
              {canManage && (
                <Button
                  variant="ghost"
                  loading={remove.isPending && remove.variables === contact.id}
                  onClick={() => remove.mutate(contact.id)}
                >
                  {t('common.actions.delete')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canManage && (
        <form
          className="form-grid"
          onSubmit={(event) => {
            event.preventDefault();
            if (value.trim()) add.mutate();
          }}
        >
          <div className="field">
            <label className="field-label" htmlFor="contact-type">
              {t('common.labels.contactType')}
            </label>
            <select
              id="contact-type"
              className="field-input"
              value={type}
              onChange={(event) => setType(event.target.value as PhoneContactType)}
            >
              {PERSON_PHONE_CONTACT_TYPES.map((option) => (
                <option key={option} value={option}>
                  {t(`people.person.contactType.${option}`)}
                </option>
              ))}
            </select>
          </div>
          <Field
            label={t('people.person.phone')}
            name="contact-value"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            dir="ltr"
          />
          <Button type="submit" variant="secondary" loading={add.isPending}>
            {t('people.contact.add')}
          </Button>
        </form>
      )}
    </section>
  );
}

function describeError(error: unknown, t: ReturnType<typeof useI18n>['t']): string {
  return error instanceof ApiError
    ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
    : t('errors.INTERNAL_ERROR');
}
